import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, X, Plus, Trash2, Save, Eye, Search, AlertTriangle, CheckCircle } from 'lucide-react';
import Layout from '../layout/DefaultLayout';
import { ConceptCreationModal } from './ConceptCreationModal';
import ConceptDetailsModal from './ConceptDetailsModal';
import api from '../../utils/api';
import JSONPretty from 'react-json-pretty';


const service = {
    createConcept: (newConceptData) =>
        api.post(`/concepts`, newConceptData),
    fetchList: async () => {
        const res = await api.get(`/concepts`);
        return res.data;
    },
    fetchDetails: async (id, code) => {
        // console.log(`Concept ${id} (${code})`);
        const res = await api.get(`/conceptDetails`, { params: { identifier: id, type: "id" } });
        return res.data;
    },
    save: async (jsonString) => {
        const res = await api.post(`/conceptDetails`, jsonString);
        return res.data;
    },
    delete: async (id) => {
        console.log(`Simulating DELETE for concept ID: ${id}`);
        await api.delete(`/conceptDetails/${id}`);
        return true;
    }
};

/**
 * Shared UI Components for UX Improvement (Replacing alerts/confirms)
 */

const NotificationBar = ({ notification, clearNotification }) => {
    if (!notification || notification.type === 'confirm') return null;

    const isError = notification.type === 'error';
    const bgColor = isError ? 'bg-red-500' : 'bg-gray-800';
    const Icon = isError ? AlertTriangle : CheckCircle;

    return (
        <div className={`fixed bottom-4 right-4 p-4 ${bgColor} text-white rounded-lg shadow-xl z-50 flex items-center space-x-3 transition-opacity duration-300`}>
            <Icon size={20} />
            <span className="text-sm font-medium">{notification.message}</span>
            <button onClick={clearNotification} className="text-white/70 hover:text-white transition">
                <X size={16} />
            </button>
        </div>
    );
};

const CustomConfirmModal = ({ notification, onConfirm, onClose }) => {
    if (!notification || notification.type !== 'confirm') return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="bg-white  rounded-xl shadow-2xl w-full max-w-sm border border-gray-200 ">
                <div className="p-5">
                    <AlertTriangle size={32} className="text-red-500 mx-auto mb-3" />
                    <p className="text-lg font-semibold text-gray-800  text-center mb-4">{notification.message}</p>
                    <p className="text-sm text-gray-600 text-center">This action cannot be undone.</p>
                </div>
                <div className="p-4 border-t border-gray-200 flex justify-center space-x-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700  bg-gray-100 rounded-lg hover:bg-gray-200  transition border border-gray-300 ">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition shadow-md">
                        Confirm Delete
                    </button>
                </div>
            </div>
        </div>
    );
};


/**
 * Main Application Component
 */
const ConceptManager = () => {

    const [concepts, setConcepts] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedConceptId, setSelectedConceptId] = useState(null);
    const [conceptJson, setConceptJson] = useState('');
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false); // Creation modal
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false); // Preview modal
    const [jsonText, setJsonText] = useState(JSON.stringify(conceptJson, null, 2));


    // Notification state for errors/successes
    const [notification, setNotification] = useState(null);
    const clearNotification = () => setNotification(null);

    const [selectedConceptForDetails, setSelectedConceptForDetails] = useState(null);

    const currentConcept = useMemo(() => {
        return concepts.find(c => c.id === selectedConceptId);
    }, [concepts, selectedConceptId]);


    /**
     * JSON VALIDATION LOGIC
     * Checks for basic structure, required fields, and hierarchical code prefixes (Concept -> Unit -> Fact).
     * @param {string} jsonString - The raw JSON from the editor.
     * @returns {string | null} Error message or null if valid.
     */
    const validateConceptJson = (jsonString) => {
        let concept;
        try {
            concept = jsonString;
        } catch (e) {
            return `JSON Parsing Error: The data is not valid JSON. Check brackets and quotes. (Details: ${e.message})`;
        }

        if (typeof concept !== 'object' || concept === null || Array.isArray(concept)) {
            return "Validation Error: The root element must be a single JSON object (Concept).";
        }

        // --- 1. PARENT LEVEL VALIDATION (Concept) ---
        if (!concept.code || typeof concept.code !== 'string' || concept.code.trim().length === 0) {
            return "Validation Error: 'code' field is mandatory and must be a non-empty string.";
        }
        const parentCode = concept.code.trim();

        if (parentCode.length !== 4) {
            return "Format Error: Concept code must be exactly 4 characters (e.g., ATOM).";
        }

        // --- 2. CHILDREN PREFIX VALIDATION (Concept Units) ---

        if (concept.conceptUnits && !Array.isArray(concept.conceptUnits)) {
            return "Structure Error: 'conceptUnits' must be an array.";
        }
        if (Array.isArray(concept.conceptUnits)) {
            for (let i = 0; i < concept.conceptUnits.length; i++) {
                const unit = concept.conceptUnits[i];

                // Unit existence/type check
                if (!unit.code || typeof unit.code !== 'string') {
                    return `Structure Error: Unit ${i + 1} must have a 'code' field (string).`;
                }

                const unitCode = unit.code.trim();

                // Unit prefix check
                if (!unitCode.startsWith(parentCode)) {
                    return `Prefix Validation Error (Unit ${i + 1}): Unit code '${unitCode}' must start with the parent concept code '${parentCode}'.`;
                }

                // --- 3. GRANDCHILDREN PREFIX VALIDATION (Concept Facts) ---
                if (unit.conceptFacts && !Array.isArray(unit.conceptFacts)) {
                    return `Structure Error: Unit ${i + 1} ('${unitCode}') has a 'conceptFacts' field that must be an array.`;
                }
                if (Array.isArray(unit.conceptFacts)) {
                    for (let j = 0; j < unit.conceptFacts.length; j++) {
                        const fact = unit.conceptFacts[j];

                        // Fact existence/type check
                        if (!fact.code || typeof fact.code !== 'string') {
                            return `Structure Error: Fact ${j + 1} in Unit ${i + 1} must have a 'code' field (string).`;
                        }

                        // Fact prefix check
                        if (!fact.code.startsWith(unitCode)) {
                            return `Prefix Validation Error (Fact ${j + 1}): Fact code '${fact.code}' must start with its parent Unit code '${unitCode}'.`;
                        }
                    }
                }
            }
        }

        // --- 4. OTHER ARRAY VALIDATIONS (Concept Chapters) ---
        if (concept.conceptChapters && !Array.isArray(concept.conceptChapters)) {
            return "Structure Error: 'conceptChapters' must be an array.";
        }
        // if (Array.isArray(concept.conceptChapters)) {
        //     for (let i = 0; i < concept.conceptChapters.length; i++) {
        //         const chapter = concept.conceptChapters[i];
        //         if (!chapter.code || typeof chapter.code !== 'string') {
        //             return `Structure Error: Chapter ${i + 1} must have a 'code' field (string).`;
        //         }
        //         if (!chapter.code.startsWith(parentCode)) {
        //             return `Prefix Validation Error (Chapter ${i + 1}): Chapter code '${chapter.code}' must start with the parent concept code '${parentCode}'.`;
        //         }
        //     }
        // }

        // Basic ID check for updates
        if (selectedConceptId && concept.id !== selectedConceptId && concept.id !== null) {
            return "ID Mismatch: The 'id' field in the JSON does not match the selected concept ID in the list. Please ensure the 'id' field is correct for updates.";
        }

        return null; // Valid
    };


    // --- API FUNCTIONS (unchanged) ---

    const fetchConceptList = useCallback(async () => {
        setIsLoadingList(true);
        try {
            const list = await service.fetchList();
            setConcepts(list);
        } catch (error) {
            console.error("Failed to fetch concept list:", error);
            setNotification({ message: 'Failed to load concepts. See console.', type: 'error' });
        } finally {
            setIsLoadingList(false);
        }
    }, []);

    const fetchConceptDetails = useCallback(async (concept) => {
        clearNotification();
        setSelectedConceptId(concept.id);
        setConceptJson(`Loading ${concept.code}...`);
        try {
            const json = await service.fetchDetails(concept.id, concept.code);
            setConceptJson(json);
        } catch (error) {
            console.error(`Failed to fetch details for ${concept.code}:`, error);
            setConceptJson(`{"error": "Failed to load concept details: ${error.message}"}`);
            setNotification({ message: `Failed to load details for ${concept.code}.`, type: 'error' });
        }
    }, []);

    const saveConcept = async () => {
        clearNotification();
        if (!conceptJson) return;

        const validationError = validateConceptJson(conceptJson);
        if (validationError) {
            setNotification({ message: validationError, type: 'error' });
            return;
        }

        setIsSaving(true);
        try {
            const savedConcept = await service.save(conceptJson);

            // Update local list
            setConcepts(prev => {
                const updatedList = prev.filter(c => c.id !== savedConcept.id); // Remove old version
                updatedList.unshift({ id: savedConcept.id, code: savedConcept.code, name: savedConcept.name, isActive: savedConcept.isActive }); // Add new version
                return updatedList;
            });
            setSelectedConceptId(savedConcept.id);

            // Re-fetch the saved JSON to ensure the returned server data is displayed
            // const newJson = await service.fetchDetails(savedConcept.id, savedConcept.code);
            setConceptJson(savedConcept);

            setNotification({ message: `Concept ${savedConcept.code} saved successfully!`, type: 'success' });

        } catch (error) {
            console.error("Save failed:", error);
            setNotification({ message: `API Save failed: ${error.message || 'Check console for network details.'}`, type: 'error' });
        } finally {
            setIsSaving(false);
        }
    };

    const confirmDelete = () => {
        if (!currentConcept || isDeleting) return;

        setNotification({
            message: `Are you sure you want to SOFT DELETE concept: ${currentConcept.code} (${currentConcept.name})?`,
            type: 'confirm',
        });
    }


    const deleteConcept = async () => {
        clearNotification(); // Clear confirm modal
        if (!currentConcept || isDeleting) return;

        setIsDeleting(true);
        try {
            await service.delete(currentConcept.id);

            // Update local list
            setConcepts(prev => {
                const updatedList = prev.filter(c => c.id !== currentConcept.id); // Remove old version
                updatedList.unshift({ id: currentConcept.id, code: currentConcept.code, name: currentConcept.name, isActive: !currentConcept.isActive }); // Add new version
                return updatedList;
            });
            setConceptJson('');

            setNotification({ message: `Concept ${currentConcept.code} deleted successfully (soft delete).`, type: 'success' });

        } catch (error) {
            console.error("Delete failed:", error);
            setNotification({ message: `Delete failed: ${error.message || 'Check console for details.'}`, type: 'error' });
        } finally {
            setIsDeleting(false);
        }
    };

    const createNewConcept = async (code, name, description) => {
        try {
            const newConcept = { code, name, description, isActive: true };
            const response = await service.createConcept(newConcept);
            const createdConcept = response.data;

            setConcepts(prev => [...prev, createdConcept]);

        } catch (err) {
            console.error("❌ API Error: Failed to create new concept.", err.response || err);
            setNotification({ message: `Concept creation for ${code} failed!`, type: 'failure' });
        }
    };

    // const createNewConcept = async (newConceptJson) => {
    //     setConceptJson(newConceptJson);
    //     await saveConcept();
    // };


    // --- UI HANDLERS ---

    const handlePreview = () => {
        clearNotification();
        const validationError = validateConceptJson(conceptJson);

        if (validationError) {
            setNotification({ message: validationError, type: 'error' });
            return;
        }

        try {
            // const parsedConcept = JSON.parse(conceptJson);
            // console.log(parsedConcept)
            if (conceptJson) {
                setSelectedConceptForDetails(conceptJson);
                setIsDetailsModalOpen(true);
            }
        } catch (error) {
            console.log(error)
            setNotification({ message: "Cannot preview: Invalid JSON format.", type: 'error' });
        }
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedConceptForDetails(null);
    };

    const filteredConcepts = concepts.filter(concept =>
        concept.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        concept.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Initial data fetch
    useEffect(() => {
        fetchConceptList();
    }, []);

    useEffect(() => {
        setJsonText(JSON.stringify(conceptJson, null, 2));
    }, [conceptJson]);


    // --- RENDER ---

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50 text-gray-900  p-2 font-sans flex flex-col">
                <h1 className="text-2xl font-bold text-gray-800 mb-2">🔗 Concept Manager</h1>
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between gap-3">
                    <h2 className="text-lg font-semibold text-gray-800 whitespace-nowrap">
                        {currentConcept ? `Editor: ${currentConcept.code}` : 'Select a Concept'}
                    </h2>
                    <div className="flex space-x-3 flex-shrink-0">
                        <button
                            onClick={handlePreview}
                            disabled={isSaving || isDeleting || !conceptJson}
                            className="flex items-center px-3 py-2 bg-gray-500 text-white text-sm font-medium rounded-lg hover:bg-gray-600 transition shadow-md disabled:opacity-50"
                        >
                            <Eye size={16} className="mr-1" /> Preview
                        </button>
                        <button
                            onClick={confirmDelete}
                            disabled={!selectedConceptId || isSaving || isDeleting}
                            className="flex items-center px-3 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition shadow-md disabled:opacity-50"
                        >
                            <Trash2 size={16} className="mr-1" /> {isDeleting ? 'Deleting...' : 'Delete'}
                        </button>
                        <button
                            onClick={saveConcept}
                            disabled={isSaving || isDeleting || !conceptJson}
                            className="flex items-center px-3 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-black transition shadow-md disabled:opacity-50"
                        >
                            <Save size={16} className="mr-1" /> {isSaving ? 'Saving...' : 'Save/Update'}
                        </button>
                    </div>
                </div>


                <div className="flex flex-1 overflow-hidden rounded-2xl shadow-xl bg-white border border-gray-200">

                    {/* Left Panel: Concept List and Search */}
                    <div className="w-full md:w-1/4 border-r border-gray-200 flex flex-col">
                        <div className="p-4 border-b border-gray-200 bg-gray-50">
                            <div className="relative mb-3">
                                <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Search Concepts..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 bg-white text-gray-800 rounded-lg focus:ring-gray-700 focus:border-gray-700 transition shadow-inner"
                                />
                            </div>
                            <div className="flex justify-between space-x-3">
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="flex-1 flex items-center justify-center p-2 bg-gray-700 text-white text-sm font-medium rounded-lg hover:bg-gray-800 transition shadow-md disabled:opacity-50"
                                >
                                    <Plus size={16} className="mr-1" /> New Concept
                                </button>
                                <button
                                    onClick={fetchConceptList}
                                    disabled={isLoadingList}
                                    className="p-2 bg-white text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 transition shadow-md disabled:opacity-50 flex items-center border border-gray-300"
                                >
                                    <RefreshCw size={14} className={isLoadingList ? "animate-spin" : ""} />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {isLoadingList && (
                                <div className="p-4 text-center text-gray-500">Loading Concepts...</div>
                            )}
                            {!isLoadingList && filteredConcepts.length === 0 && (
                                <div className="p-4 text-center text-gray-500">No concepts found.</div>
                            )}
                            {!isLoadingList && filteredConcepts.map((concept) => (
                                <div
                                    key={concept.id}
                                    onClick={() => fetchConceptDetails(concept)}
                                    className={`p-3 cursor-pointer border-b border-gray-100 transition duration-150 ease-in-out
                    ${selectedConceptId === concept.id
                                            ? 'bg-gray-200 border-l-4 border-gray-800'
                                            : 'hover:bg-gray-100'
                                        }`}
                                >
                                    <div className="flex justify-between items-center font-bold text-sm text-gray-800 w-full">
                                        <span>{concept.name}</span>
                                        <span
                                            className={`px-2 py-1 rounded-full text-xs font-semibold ${concept.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                }`}
                                        >
                                            {concept.isActive ? 'active' : 'inactive'}
                                        </span>
                                    </div>

                                    <div className="text-xs text-gray-600 truncate">{concept.code}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Right Panel: JSON Editor and Actions */}
                    <div className="w-full md:w-3/4 flex flex-col">

                        {/* <JSONPretty
                            data={conceptJson}
                            className="json-pretty whitespace-pre-wrap break-words"
                        /> */}

                        {
                            <textarea
                                value={jsonText}
                                onChange={(e) => {
                                    setJsonText(e.target.value);
                                    try {
                                        const parsed = JSON.parse(e.target.value);
                                        setConceptJson(parsed);
                                        clearNotification();
                                    } catch {
                                        // Optional: show error
                                    }
                                }}
                                className="w-full h-full p-4 resize-none font-mono text-sm bg-white text-gray-800 border rounded outline-none whitespace-pre-wrap break-words"
                                spellCheck="false"
                            />

                        }
                    </div>
                </div>

                {/* Modals and Notifications */}
                <ConceptCreationModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onCreate={createNewConcept}
                />

                <ConceptDetailsModal
                    isOpen={isDetailsModalOpen}
                    onClose={handleCloseDetailsModal}
                    concept={jsonText}
                />

                <NotificationBar
                    notification={notification}
                    clearNotification={clearNotification}
                />

                <CustomConfirmModal
                    notification={notification}
                    onConfirm={deleteConcept}
                    onClose={clearNotification}
                />

            </div>
        </Layout >
    );
};

export default ConceptManager;